import { useId, useState } from "react";
import type { ReactNode } from "react";

import { Icone } from "./Icone";
import { Grupo } from "./NavegacaoArvore";
import { NavegacaoFaixa } from "./NavegacaoFaixa";
import { temDestino } from "./navegacaoMapa";
import type { GrupoDeNavegacao } from "./navegacaoMapa";
import { gravarRecolhidos, lerRecolhidos, reabrirOCaminhoDaRota } from "./navegacaoRecolhidos";

import "./Navegacao.css";

/*
 * ⚠️ A ORDEM DAS TRÊS FOLHAS DA NAVEGAÇÃO É A ORDEM DESTES IMPORTS — moldura,
 * árvore, faixa —, e é a que os três blocos tinham dentro de `ui.css`. Onde a
 * disputa de estilo é EMPATE, é ela que decide. Ver o quadro em `Navegacao.css`.
 */

export type {
  GrupoDeNavegacao,
  ItemDeNavegacao,
  SubmoduloDeNavegacao,
} from "./navegacaoMapa";

/**
 * A navegação lateral — e a distinção que ela precisa deixar clara.
 *
 * <h2>O recorte deste componente</h2>
 *
 * Este arquivo tinha <b>804 linhas</b> e era o maior componente do sistema. O
 * ADR-0013 §2 fixou o teto em 300 e nomeou o corte: <b>coluna · gaveta · faixa
 * recolhida</b>. Hoje ele é a MOLDURA — a coluna, o botão que troca a forma
 * dela e o rodapé —, e o resto mora ao lado:
 *
 * <ul>
 *   <li>{@link ./navegacaoMapa} — os tipos dos três níveis e as perguntas que
 *       se fazem sobre o mapa. Nenhuma delas desenha nada;</li>
 *   <li>{@link ./navegacaoRecolhidos} — o que sobrevive ao recarregamento;</li>
 *   <li>`NavegacaoArvore.tsx` — a lista esticada, nos três níveis;</li>
 *   <li>`NavegacaoFaixa.tsx` — a faixa de ícones.</li>
 * </ul>
 *
 * <p><b>A gaveta do celular não aparece nesta lista porque não é deste
 * componente</b>: ela é a mesma navegação enquadrada pela `Estrutura`, em
 * `apps/plantus/src/nucleo/layout`. É de lá que vêm o `Esc`, o corpo travado e
 * a saída do caminho do TAB.
 *
 * <h2>Isto não é controle de acesso, e não pode parecer</h2>
 *
 * O ADR-0004 §6 decidiu que o menu é <b>construído a partir das permissões</b>:
 * rota sem permissão não é rota que redireciona, é rota que não foi criada.
 * Enquanto o RBAC não existe, esta navegação lista o que <b>foi construído</b>,
 * e nada mais.
 *
 * <p>Por isso o item indisponível <b>não é um link desabilitado</b>: ele não é
 * link nenhum, não recebe foco de teclado e não tem destino. Não há rota
 * desprotegida atrás dele porque não há rota.
 *
 * <p><b>"Em construção" ≠ "você não tem acesso".</b> São coisas diferentes, e
 * confundi-las envenena a leitura da tela no dia em que o RBAC chegar — quando
 * o que a pessoa não pode ver simplesmente <b>não aparece</b>, e o que ainda
 * não existe continua aparecendo esmaecido.
 *
 * <h2>Cada nível é um tópico que abre e fecha</h2>
 *
 * Com tudo aberto de uma vez a lista cresce a cada tela nova, e quem trabalha
 * num setor rola por três outros para chegar ao seu. Recolher resolve isso —
 * mas <b>esconder é a mesma operação que perder</b>, e por isso o recolhimento
 * é sempre <b>escolha de quem usa</b>, nunca padrão. Vale igual para setor e
 * para módulo:
 *
 * <ul>
 *   <li><b>tudo nasce aberto.</b> No dia da atualização ninguém perde destino
 *       que já sabia onde ficava; quem quiser menos lista, recolhe;</li>
 *   <li>o caminho da <b>tela atual abre INTEIRO</b> — setor <b>e</b> módulo —
 *       mesmo guardado como recolhido. Em
 *       `/producao/materias-primas/7/etiqueta`, Produção e Matérias-primas
 *       estão abertos: quem abre a gaveta tem de ver onde está, e preferência
 *       velha não pode esconder a tela em que a pessoa já se encontra;</li>
 *   <li>o que abre e fecha é <b>{@code <button>}</b> com {@code aria-expanded} —
 *       um {@code <div>} clicável não recebe foco de teclado nem se anuncia
 *       como controle, e esta gaveta é operada por teclado;</li>
 *   <li>recolher o setor ou o módulo em que se está é permitido, mas
 *       <b>navegar para dentro dele o traz de volta</b>: nenhuma tela fica sem
 *       caminho visível até ela.</li>
 * </ul>
 *
 * <p><b>Setor sem item não vira tópico.</b> Ver `NavegacaoArvore.tsx`. Pelo
 * mesmo motivo, item sem filho não ganha botão de disclosure: interruptor que
 * não acende nada promete conteúdo e entrega o mesmo lugar.
 *
 * <h2>Duas formas: esticada e faixa de ícones</h2>
 *
 * No desktop a coluna tem um botão que a recolhe a uma <b>faixa estreita de
 * ícones</b> — ela não some por inteiro, porque menu que some não é menu
 * recolhido, é menu perdido: para voltar seria preciso saber que existe um
 * botão em algum lugar da barra.
 *
 * <ul>
 *   <li><b>Esticada é o padrão.</b> Quem nunca tocar no botão nunca vê a
 *       faixa — a mesma regra dos tópicos, pela mesma razão: esconder é a
 *       mesma operação que perder, e perder tem de ser escolha;</li>
 *   <li><b>só entra na faixa quem tem destino.</b> Item em construção fica
 *       de fora: um ícone que não leva a lugar nenhum ocupa 44px prometendo
 *       tela, e ali não há onde escrever "em construção";</li>
 *   <li><b>o ícone tem `aria-label` e dica visível</b> no ponteiro e no foco.
 *       Ícone sem rótulo é adivinhação, e quem trabalha de luva no corredor
 *       não tem tempo de adivinhar;</li>
 *   <li><b>o item da tela atual continua marcado</b> — na faixa ele é a única
 *       pista de onde a pessoa está.</li>
 * </ul>
 *
 * <p>Quem decide se a faixa vale é <b>quem enquadra</b>: abaixo de 56rem a
 * navegação é gaveta, e gaveta não recolhe. Por isso `recolhida` e
 * `aoAlternarRecolhimento` chegam de fora, junto com a persistência da
 * preferência — ver `Estrutura` em `apps/plantus/src/nucleo/layout`.
 */

interface Props {
  grupos: readonly GrupoDeNavegacao[];
  /** Caminho atual, para marcar o item ativo. */
  rotaAtual: string;
  /** Recebe a rota; quem chama decide como navegar. */
  aoNavegar(rota: string): void;
  /**
   * A navegação está em <b>faixa de ícones</b>?
   *
   * <p>Quem decide é o enquadramento, não este componente: a faixa só faz
   * sentido onde a navegação é COLUNA, e abaixo de 56rem ela é gaveta. Ver a
   * seção "esticada e recolhida" no topo do arquivo.
   */
  recolhida?: boolean;
  /**
   * O botão de recolher e esticar. <b>Ausente é o caso normal do celular</b>:
   * sem esta função o botão não é desenhado, e não é desenhado significa que
   * ele não está no DOM nem no caminho do TAB — não que está escondido por
   * CSS, que é a falha que esta gaveta já pagou duas vezes.
   */
  aoAlternarRecolhimento?: () => void;
  /** Rodapé da navegação — versão, ambiente. */
  children?: ReactNode;
}

export function Navegacao({
  grupos,
  rotaAtual,
  aoNavegar,
  recolhida = false,
  aoAlternarRecolhimento,
  children,
}: Props) {
  // O `aria-controls` precisa de um id único no documento, e o caminho
  // sozinho colidiria se a navegação fosse desenhada duas vezes na mesma
  // página — coluna fixa e gaveta, por exemplo.
  const prefixo = useId();

  /*
   * O que a pessoa recolheu SOBREVIVE ao recarregamento — e o que se guarda é
   * só isto: uma lista de caminhos (`setor` ou `setor/modulo`). Nada de
   * negócio, nada de sessão.
   *
   * Guardar é o que faz a cascata valer alguma coisa. Sem persistir, quem
   * abre o sistema do celular várias vezes ao dia refaria a mesma arrumação a
   * cada acesso, e desistiria de arrumar — que é o mesmo que não ter o
   * recurso. O risco de guardar é a preferência velha esconder algo; ele está
   * respondido em {@link CHAVE_RECOLHIDOS} (guarda-se o fechado, não o
   * aberto) e na linha abaixo, que reabre o caminho da tela atual antes de
   * qualquer coisa aparecer.
   *
   * Vários abertos ao mesmo tempo, de propósito: acordeão de um-só fecha o
   * setor anterior sem ninguém pedir, e quem confere um lote olhando a
   * habilitação da empresa perde o de cima ao abrir o de baixo.
   */
  const [recolhidos, definirRecolhidos] = useState<ReadonlySet<string>>(() =>
    reabrirOCaminhoDaRota(lerRecolhidos(), grupos, rotaAtual),
  );

  /*
   * Reabre ao MUDAR de rota — não a cada renderização.
   *
   * Recolher o setor ou o módulo em que se está é direito de quem usa: a tela
   * some da lista, e está certo. O que não pode é a pessoa navegar para
   * dentro de um tópico recolhido e não achar por onde entrou.
   *
   * O ajuste acontece na renderização, e não num efeito, porque o estado
   * certo já vale no primeiro quadro: num efeito o setor apareceria fechado e
   * abriria um quadro depois, piscando justamente para quem acabou de chegar
   * ali.
   */
  const [rotaVista, definirRotaVista] = useState(rotaAtual);
  if (rotaAtual !== rotaVista) {
    definirRotaVista(rotaAtual);
    const proximos = reabrirOCaminhoDaRota(recolhidos, grupos, rotaAtual);
    if (proximos !== recolhidos) definirRecolhidos(proximos);
  }

  /*
   * A forma com função, e não `new Set(recolhidos)` a partir do fechamento.
   *
   * Medido no navegador: recolher três setores dentro do mesmo lote de
   * atualização do React fazia os três lerem o MESMO estado anterior, e só o
   * último valia — dois cabeçalhos continuavam abertos depois de clicados.
   * Dedo humano não clica três vezes no mesmo quadro; teclado repetindo e
   * script de verificação, sim.
   *
   * A gravação mora aqui dentro porque é aqui que existe o valor RESULTANTE.
   * Grava só quando a pessoa mexe: quem nunca tocou num cabeçalho não deixa
   * nada guardado, e continua verdade que a navegação sozinha não escreve no
   * armazenamento do cliente.
   */
  const alternar = (caminho: string) =>
    definirRecolhidos((atuais) => {
      const proximos = new Set(atuais);
      if (!proximos.delete(caminho)) proximos.add(caminho);
      gravarRecolhidos(proximos);
      return proximos;
    });

  const estaAberto = (caminho: string) => !recolhidos.has(caminho);

  const desenhar = (grupo: GrupoDeNavegacao) => (
    <Grupo
      key={grupo.id}
      grupo={grupo}
      rotaAtual={rotaAtual}
      aoNavegar={aoNavegar}
      estaAberto={estaAberto}
      alternar={alternar}
      prefixo={prefixo}
    />
  );

  const setores = grupos.filter((g) => g.natureza === "setor");
  const transversais = grupos.filter((g) => g.natureza === "transversal");

  /*
   * O botão vive DENTRO da navegação, e não no cabeçalho.
   *
   * Ele muda a forma desta coluna, e controle que mora longe do que ele muda
   * é controle que ninguém liga a nada. Recolhido, ele é a primeira coisa da
   * faixa — o caminho de volta fica no mesmo lugar em que a ida ficava.
   *
   * <b>Sem `aria-expanded`.</b> Nada aqui é escondido para ser revelado: a
   * coluna troca de forma, e os destinos continuam todos na tela. O estado é
   * dito UMA vez, pelo nome do botão — "Recolher" só existe quando está
   * esticada —, que é a mesma regra da seta `aria-hidden` dos tópicos.
   */
  const alternador = aoAlternarRecolhimento !== undefined && (
    <button
      type="button"
      className="ui-navegacao__alternador"
      aria-label={recolhida ? "Esticar a navegação" : "Recolher a navegação"}
      onClick={aoAlternarRecolhimento}
    >
      <Icone nome={recolhida ? "esticar" : "recolher"} />
    </button>
  );

  if (recolhida) {
    return (
      <nav className="ui-navegacao ui-navegacao--faixa" aria-label="Setores do sistema">
        {alternador}
        <NavegacaoFaixa grupos={setores} rotaAtual={rotaAtual} aoNavegar={aoNavegar} />
        {temDestino(transversais) && temDestino(setores) && (
          <hr className="ui-navegacao__regra" />
        )}
        <NavegacaoFaixa grupos={transversais} rotaAtual={rotaAtual} aoNavegar={aoNavegar} />
      </nav>
    );
  }

  return (
    <nav className="ui-navegacao" aria-label="Setores do sistema">
      {alternador}

      {setores.map(desenhar)}

      {transversais.length > 0 && (
        <>
          {/* Regra visual, não decorativa: transversal não é setor da
              indústria, e agrupá-los junto sugeriria que é. */}
          <hr className="ui-navegacao__regra" />
          {transversais.map(desenhar)}
        </>
      )}

      {children !== undefined && <div className="ui-navegacao__rodape">{children}</div>}
    </nav>
  );
}
